import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'

// Helper function to recalculate start and end times based on cumulative durations
// Preserves the order of tiles passed in (doesn't sort by old startTime)
const recalculateTimes = (tiles) => {
  let currentStart = 0
  return tiles.map((tile) => {
    const duration = tile.duration || 10 // Default 10 minutes
    const startTime = currentStart
    const endTime = startTime + duration - 1 // End is inclusive (0-10 means 0 through 10)
    
    const updatedTile = {
      ...tile,
      startTime,
      endTime,
      duration
    }
    
    // Next tile starts at this tile's end time + 1
    currentStart = endTime + 1
    return updatedTile
  })
}

const usePracticeStore = create(
  subscribeWithSelector((set, get) => ({
    // Practice tiles
    tiles: [],
    
    // Practice notes
    notes: '',
    
    // Session name
    sessionName: 'Practice Session Title',

    // Actions
    addTile: (tile) => set((state) => {
      const newTiles = [...state.tiles, tile]
      // Sort by startTime to maintain chronological order before recalculating
      const sortedTiles = newTiles.sort((a, b) => (a.startTime || 0) - (b.startTime || 0))
      return { tiles: recalculateTimes(sortedTiles) }
    }),

    addTileAt: (tile, targetIndex) => set((state) => {
      // Get current tiles sorted by startTime to maintain display order
      const sortedTiles = [...state.tiles].sort((a, b) => (a.startTime || 0) - (b.startTime || 0))
      // Insert the new tile at the target position
      sortedTiles.splice(targetIndex, 0, tile)
      // Recalculate times based on new order (preserves the order we just set)
      return { tiles: recalculateTimes(sortedTiles) }
    }),

    updateTile: (updatedTile) => set((state) => {
      // Update the tile
      const updatedTiles = state.tiles.map(tile =>
        tile.id === updatedTile.id ? updatedTile : tile
      )
      // Sort by startTime to maintain chronological order before recalculating
      const sortedTiles = updatedTiles.sort((a, b) => (a.startTime || 0) - (b.startTime || 0))
      // Recalculate all start and end times to ensure cumulative durations
      return { tiles: recalculateTimes(sortedTiles) }
    }),

    deleteTile: (tileId) => set((state) => {
      const remainingTiles = state.tiles.filter(tile => tile.id !== tileId)
      // Sort by startTime to maintain chronological order before recalculating
      const sortedTiles = remainingTiles.sort((a, b) => (a.startTime || 0) - (b.startTime || 0))
      // Recalculate times after deletion
      return { tiles: recalculateTimes(sortedTiles) }
    }),

    clearTiles: () => set({ tiles: [] }),

    setTiles: (tiles) => set((state) => {
      // Sort by startTime to maintain chronological order before recalculating
      const sortedTiles = [...tiles].sort((a, b) => (a.startTime || 0) - (b.startTime || 0))
      return { tiles: recalculateTimes(sortedTiles) }
    }),
    
    reorderTile: (draggedTileId, targetIndex) => set((state) => {
      const sortedTiles = [...state.tiles].sort((a, b) => (a.startTime || 0) - (b.startTime || 0))
      const draggedTileIndex = sortedTiles.findIndex(t => t.id === draggedTileId)
      
      if (draggedTileIndex === -1) {
        return state // Tile not found
      }
      
      // Remove the dragged tile from its current position
      const [draggedTile] = sortedTiles.splice(draggedTileIndex, 1)
      
      // Adjust target index if dragging from before the target
      const adjustedTargetIndex = draggedTileIndex < targetIndex 
        ? targetIndex - 1 
        : targetIndex
      
      // Insert it at the target position
      sortedTiles.splice(adjustedTargetIndex, 0, draggedTile)
      
      // Recalculate times based on new order
      return { tiles: recalculateTimes(sortedTiles) }
    }),
    
    setNotes: (notes) => set({ notes }),
    
    clearNotes: () => set({ notes: '' }),
    
    setSessionName: (name) => set({ sessionName: name }),

    // Track original loaded session for save logic
    originalLoadedSession: { id: null, name: null },
    
    setOriginalLoadedSession: (session) => set({ originalLoadedSession: session }),
    
    resetSessionName: () => set({ 
      sessionName: 'Practice Session Title',
      originalLoadedSession: { id: null, name: null }
    })
  }))
)

export default usePracticeStore

